import { Controller } from '@hotwired/stimulus'
import swapText from '../utils/swap_text'
import visibilityLoop from '../utils/visibility_loop'

// Drives the checkout mockup in the online ordering hero: three menu items drop
// into the cart one at a time, the total climbs with them, the order is placed,
// and after a beat the whole thing resets and runs again.
//
// The prices live on the markup (`data-price`) so the running total can never
// disagree with the rows the reader is looking at.
//
//   <div data-controller="ordering-cart">
//     <li data-ordering-cart-target="item" data-price="16.50">…</li>
//     <span data-ordering-cart-target="count">…</span>
//     <span data-ordering-cart-target="total">…</span>
//     <span data-ordering-cart-target="cta">Place order</span>
//
export default class extends Controller {
  static targets = ['item', 'count', 'total', 'cta']
  static values = {
    // Gap between one item landing in the cart and the next.
    step: { type: Number, default: 950 },
    // How long the placed-order state holds before the loop restarts.
    hold: { type: Number, default: 2800 }
  }

  connect() {
    this.timers = []

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.settle()
      return
    }

    this.stopLoop = visibilityLoop(this.element, {
      start: () => this.run(),
      stop: () => this.clearTimers()
    })
  }

  disconnect() {
    if (this.stopLoop) this.stopLoop()
    this.clearTimers()
  }

  // Park on a full cart: the same story, told in one frame.
  settle() {
    this.itemTargets.forEach((item) => item.classList.add('is-added'))
    this.render(this.itemTargets.length)
  }

  run() {
    this.reset()

    this.itemTargets.forEach((item, index) => {
      this.timers.push(
        setTimeout(() => {
          item.classList.add('is-added')
          this.render(index + 1)
        }, this.stepValue * (index + 1))
      )
    })

    const placedAt = this.stepValue * (this.itemTargets.length + 1)

    this.timers.push(
      setTimeout(() => {
        if (this.hasCtaTarget) this.ctaTarget.classList.add('is-armed')
      }, placedAt - this.stepValue / 2)
    )

    this.timers.push(
      setTimeout(() => this.element.classList.add('is-placed'), placedAt)
    )

    this.timers.push(setTimeout(() => this.run(), placedAt + this.holdValue))
  }

  reset() {
    this.clearTimers()
    this.element.classList.remove('is-placed')
    this.itemTargets.forEach((item) => item.classList.remove('is-added'))
    if (this.hasCtaTarget) this.ctaTarget.classList.remove('is-armed')
    this.render(0)
  }

  render(added) {
    const total = this.itemTargets
      .slice(0, added)
      .reduce((sum, item) => sum + parseFloat(item.dataset.price || 0), 0)

    if (this.hasCountTarget) {
      this.countTarget.textContent =
        added === 0 ? 'Cart empty' : `${added} ${added === 1 ? 'item' : 'items'}`
    }

    if (this.hasTotalTarget) {
      swapText(this.totalTarget, `$${total.toFixed(2)}`)
    }
  }

  clearTimers() {
    this.timers.forEach((t) => clearTimeout(t))
    this.timers = []
  }
}
